// Filter framework. M4 foundation.
//
// A filter is a pure function from (PixelBuffer, params, optional Selection) →
// new PixelBuffer. Filters MUST NOT mutate the input buffer, and must not
// touch the DOM. They live at layer 1 of the architecture (Pixel Core) and
// are consumed by ApplyFilterCommand at layer 4 (Commands).
//
// Selection awareness is handled by the helper `applyFilterInPlace` below,
// not by the filter implementations themselves. A filter sees the full
// buffer and operates on every pixel; the helper then lerps between the
// original and the filtered result using the selection mask as a per-pixel
// weight, so pixels outside the selection are unchanged and feathered
// boundaries smoothly mix.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";

export interface Filter<TParams = unknown> {
  /** Stable identifier, e.g. "gaussian-blur". */
  readonly id: string;
  /** Human-readable label for menus, e.g. "Gaussian Blur". */
  readonly displayName: string;
  /** Defaults applied when the user hasn't tweaked any slider yet. */
  readonly defaultParams: TParams;
  /**
   * Compute the filtered pixels. MUST be pure: never mutate `input`, never
   * read or write DOM, never schedule timers. The `selection` argument is
   * passed through for filters that want to optimize (e.g. bounding-box
   * restriction) but most filters will ignore it and let `applyFilterInPlace`
   * handle masking.
   */
  apply(
    input: PixelBuffer,
    params: TParams,
    selection: Selection | null,
  ): PixelBuffer;
}

/**
 * Run `filter` against `buffer`, then write the result back into the same
 * buffer, respecting `selection`:
 *
 *   - If `selection` is null, the entire buffer is replaced.
 *   - Otherwise, every pixel where the selection mask is non-zero is lerped
 *     between the original and the filtered value using mask/255 as the
 *     weight. Pixels with mask == 0 are unchanged.
 *
 * The selection's document dimensions are expected to match the buffer
 * dimensions; if they do not, the buffer is treated as if the selection
 * covered the overlap region only (out-of-range queries return 0 from
 * Selection.contains).
 *
 * Calls `buffer.markDirty()` once at the end.
 */
export function applyFilterInPlace<P>(
  filter: Filter<P>,
  buffer: PixelBuffer,
  params: P,
  selection: Selection | null,
): void {
  const filtered = filter.apply(buffer, params, selection);
  if (filtered.width !== buffer.width || filtered.height !== buffer.height) {
    throw new Error(
      `applyFilterInPlace: filter ${filter.id} returned a ${filtered.width}x${filtered.height} buffer, expected ${buffer.width}x${buffer.height}`,
    );
  }
  const dst = buffer.data;
  const src = filtered.data;
  if (selection === null) {
    dst.set(src);
    buffer.markDirty();
    return;
  }
  const w = buffer.width;
  const h = buffer.height;
  for (let y = 0; y < h; y++) {
    const row = y * w;
    for (let x = 0; x < w; x++) {
      const m = selection.contains(x, y);
      if (m === 0) continue;
      const i = (row + x) * 4;
      if (m === 255) {
        dst[i] = src[i] ?? 0;
        dst[i + 1] = src[i + 1] ?? 0;
        dst[i + 2] = src[i + 2] ?? 0;
        dst[i + 3] = src[i + 3] ?? 0;
        continue;
      }
      const t = m / 255;
      const inv = 1 - t;
      const dr = dst[i] ?? 0;
      const dg = dst[i + 1] ?? 0;
      const db = dst[i + 2] ?? 0;
      const da = dst[i + 3] ?? 0;
      const sr = src[i] ?? 0;
      const sg = src[i + 1] ?? 0;
      const sb = src[i + 2] ?? 0;
      const sa = src[i + 3] ?? 0;
      dst[i] = Math.round(dr * inv + sr * t);
      dst[i + 1] = Math.round(dg * inv + sg * t);
      dst[i + 2] = Math.round(db * inv + sb * t);
      dst[i + 3] = Math.round(da * inv + sa * t);
    }
  }
  buffer.markDirty();
}
